import React from 'react';
import { flexRender, type Cell, type ColumnDef, type Row } from '@tanstack/react-table';
import { Code2, Maximize2 } from 'lucide-react';
import { CELL_EXPAND_BUTTON_CLASS, CELL_EXPAND_WRAPPER_CLASS } from './cells/cellExpandButton';
import { pinnedColumnBorderStyle } from '../hooks/useColumnPinning';
import { looksLikeJsonString } from '../utils/jsonDetection';
import { shouldShowExpandIconForQueryResults } from '../../table-explorer/utils/expandedEditDetection';
import { formatNumericDisplay, type NumericGroupingMode } from '../utils/columnAlignment';
import { resolveCellFill, MEANING_OUTLINE_CLASS } from '../utils/selectionAffordance';
import { InlineCellEditor } from '../../query-editor/components/InlineCellEditor';
import type { ResultsGridEditMode } from './ResultsGrid';

/** The column metadata a rendered cell needs (subset of ResultsGrid's normalized columns). */
export type ResultsGridRowColumn = {
  id: string;
  key: string;
  name: string;
  dataType?: string;
  nullable?: boolean;
  sourceSchema?: string;
  sourceTable?: string;
  sourceColumn?: string;
};

type InlineSmartContext = React.ComponentProps<typeof InlineCellEditor>['smartContext'];

/**
 * Everything a data row needs from the grid, bundled in ONE memoized object so
 * the row component can be `React.memo` with default shallow comparison.
 *
 * Identity contract (this is what makes scrolling cheap): the grid rebuilds
 * this object ONLY when something that affects row rendering changes — column
 * sizing/pinning, cell selection, edit-mode signals (`editSignature`), column
 * metadata. It must NOT change on scroll. Mutable-per-render values (the
 * parent-supplied `editMode` object) are therefore read through stable getters
 * backed by refs, with `editSignature` forcing a rebuild when their *rendered
 * output* may differ.
 */
export type ResultsGridRowCtx = {
  columnById: Map<string, ResultsGridRowColumn>;
  numericColumnIds: Set<string>;
  groupedNumericColumnIds: Set<string>;
  /** PP-01 — how grouped columns render; read once by the grid, never per cell. */
  numericGrouping: NumericGroupingMode;
  minColumnWidth: number;
  maxAutoColumnWidth: number;
  rowHeight: number;
  /** Changes whenever edit-mode state that affects cell paint changes (mode toggle, pending count, save, inline commit). */
  editSignature: string;
  /** Pinned columns' sticky placement: side + the CSS variable (set on the
   *  <table> by the grid) carrying the live offset, + whether this column is
   *  the boundary one (gets the pinned-edge border). Width/offset changes
   *  flow through CSS — rows never re-render for a resize drag. */
  pinnedStyleByColumnId: Map<string, { side: 'left' | 'right'; varName: string; isEdge: boolean }>;
  isCellSelected: (rowIndex: number, columnId: string) => boolean;
  onCellMouseDown: (rowIndex: number, columnId: string, event: React.MouseEvent) => void;
  onCellMouseEnter: (rowIndex: number, columnId: string) => void;
  /** Latest edit-mode snapshot (ref-backed so ctx identity survives parent re-renders). */
  getEditMode: () => ResultsGridEditMode | undefined;
  onRowClick: (actualRowIndex: number, rowData: Record<string, unknown>) => void;
  onRowDoubleClick: (actualRowIndex: number, rowData: Record<string, unknown>) => void;
  onStartInlineEdit: (actualRowIndex: number, columnId: string) => void;
  onInlineEditCommit: (rowData: Record<string, unknown>, columnId: string, originalValue: unknown, newValue: unknown) => void;
  onInlineEditCancel: () => void;
  buildSmartContext: (columnId: string, rowData: Record<string, unknown>) => InlineSmartContext;
  onOpenExpandedText: (title: string, value: string, columnId?: string, rowData?: Record<string, unknown>, sourceRowIndex?: number) => void;
  /** Identity-only: the TanStack column defs the cells render through
   *  (see `renderCellContent`). Carried so a columnDefs rebuild with unchanged
   *  row data (e.g. a guessed-JSON classification flipping as rows stream in)
   *  invalidates the ctx and repaints visible rows — a REPAINT, not a remount:
   *  `renderCellContent` keeps the cell subtree's position stable so per-cell
   *  state survives. Not read by the row itself. */
  columnDefs: readonly ColumnDef<Record<string, unknown>>[];
};

type ResultsGridDataRowProps = {
  row: Row<Record<string, unknown>>;
  actualRowIndex: number;
  isSelected: boolean;
  /** Column being inline-edited in THIS row, else null. */
  editingColumnId: string | null;
  /** Column whose (grid-level, delegated) context menu is open in THIS row, else null. */
  menuColumnId: string | null;
  /** Digest of THIS row's selected columns ("" = none) — the grid derives it
   *  from the selection Set so a drag re-renders only rows whose own
   *  membership changed (ctx reads selection through a stable ref). */
  selectionSignature: string;
  ctx: ResultsGridRowCtx;
};

function cellTitle(value: unknown): string {
  if (value === null || value === undefined) return 'NULL';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * One virtualized data row of the Query Editor results grid.
 *
 * `React.memo` is the core of the grid's scroll performance: while scrolling,
 * only rows entering the overscan window mount — rows already on screen skip
 * re-rendering entirely. The table's `data` is the visible slice (so the
 * TanStack row model stays tiny), which means the `row` WRAPPER object is
 * recreated every scroll; the comparator below therefore keys on the
 * underlying `row.original` (stable per data row) plus `row.id` (the absolute
 * row index) instead of the wrapper identity. A memo-hit keeps the previous
 * wrapper rendered — safe because cells resolve values from `row.original`
 * and columns/table are stable references.
 *
 * The per-cell Radix context menu that used to live here is delegated to ONE
 * grid-level menu (see ResultsGrid) — cells only carry `data-row-index` /
 * `data-column-id` for it to read from the right-click target.
 */
function resultsGridDataRowPropsEqual(prev: ResultsGridDataRowProps, next: ResultsGridDataRowProps): boolean {
  return (
    prev.row.original === next.row.original &&
    prev.row.id === next.row.id &&
    prev.actualRowIndex === next.actualRowIndex &&
    prev.isSelected === next.isSelected &&
    prev.editingColumnId === next.editingColumnId &&
    prev.menuColumnId === next.menuColumnId &&
    prev.selectionSignature === next.selectionSignature &&
    prev.ctx === next.ctx
  );
}

/**
 * Render one cell's content WITHOUT letting the column's `cell` renderer become
 * a React element type.
 *
 * `flexRender(cell.column.columnDef.cell, ctx)` does
 * `React.createElement(Comp, props)` for any function, so the renderer closure
 * itself is the element type. `useCellRenderer` rebuilds those closures inside
 * a `useMemo` with ~35 dependencies, and several of them change identity with
 * no user input at all — most commonly the terminal result-set write that
 * lands after a query's first paint. A new closure is a DIFFERENT type at the
 * same position, so React unmounts the whole cell subtree and mounts a fresh
 * one, discarding every piece of state inside it: an open spatial peek or JSON
 * peek popover (`useState(false)`), a foreign-key popover, a loaded array-FK
 * cell. The user sees a popover they just opened close by itself mid-read.
 *
 * Calling the renderer as a plain function inlines its tree under this stable
 * `<td>` instead, so the popover keeps its position — and its state — across
 * every columnDefs rebuild. Table Explorer already renders cells this way
 * (`TableExplorerGridRow`), which is why the same popovers are stable there;
 * this brings the two grids back to parity.
 *
 * PRECONDITION: cell renderers must not call hooks. They are plain functions
 * here, not components, so a hook inside one would be a conditional hook call.
 * Today the only renderer reaching this row is `useCellRenderer`'s, which is
 * hook-free — keep it that way.
 */
function renderCellContent(cell: Cell<Record<string, unknown>, unknown>): React.ReactNode {
  const renderer = cell.column.columnDef.cell;
  if (typeof renderer === 'function') {
    return renderer(cell.getContext()) as React.ReactNode;
  }
  return flexRender(renderer, cell.getContext());
}

export const ResultsGridDataRow = React.memo(function ResultsGridDataRow({
  row,
  actualRowIndex,
  isSelected,
  editingColumnId,
  menuColumnId,
  ctx,
}: ResultsGridDataRowProps) {
  // `selectionSignature` is deliberately not destructured: cells read the
  // selection via ctx.isCellSelected (a stable ref-backed getter); the prop
  // exists purely so the memo comparator re-renders this row when its own
  // selection membership changes.
  const {
    columnById,
    numericColumnIds,
    groupedNumericColumnIds,
    numericGrouping,
    minColumnWidth,
    maxAutoColumnWidth,
    rowHeight,
  } = ctx;
  const editMode = ctx.getEditMode();
  // Zebra by ABSOLUTE row index so a row keeps its stripe while scrolling
  // (window-relative parity made stripes crawl under the data by one row per
  // row scrolled — and defeated row memoization).
  const zebraEven = actualRowIndex % 2 === 0;

  return (
    <tr
      className={`
        border-b border-border/50
        transition-colors cursor-pointer
        ${isSelected ? 'bg-primary/20 ring-1 ring-primary/50 ring-inset' : ''}
        ${!isSelected && zebraEven ? 'bg-background hover:bg-selection' : ''}
        ${!isSelected && !zebraEven ? 'bg-muted/20 hover:bg-selection' : ''}
      `}
      style={{ height: rowHeight }}
      onClick={() => ctx.onRowClick(actualRowIndex, row.original)}
      onDoubleClick={() => ctx.onRowDoubleClick(actualRowIndex, row.original)}
      aria-selected={isSelected}
      data-testid={`results-grid-row-${actualRowIndex}`}
    >
      {row.getVisibleCells().map((cell) => {
        const pinInfo = ctx.pinnedStyleByColumnId.get(cell.column.id);
        const pinnedCellStyle = pinInfo ? {
          position: 'sticky' as const,
          [pinInfo.side]: `var(${pinInfo.varName})`,
          zIndex: 20,
          background: zebraEven ? 'var(--background)' : 'color-mix(in srgb, var(--muted) 20%, var(--background))',
          ...(pinInfo.isEdge ? pinnedColumnBorderStyle(pinInfo.side) : {}),
        } : {};
        const columnId = cell.column.id;
        const isEditing = editingColumnId === columnId;
        // "Can this cell be edited?" — works even when edit mode is off
        const canEditCell = editMode?.hasEditableColumns && editMode.isColumnEditable(columnId);
        // "Can inline editing start?" — requires edit mode to be on
        const isCellInlineEditable = editMode?.isEditMode && canEditCell;

        // Check for pending value — use it for display instead of original
        let hasPendingChange = false;
        let pendingValue: unknown | undefined;
        if (editMode?.isEditMode) {
          const pk = editMode.getPrimaryKeyForCell(row.original, columnId);
          if (pk) {
            pendingValue = editMode.getCellPendingValue(pk, columnId);
            hasPendingChange = pendingValue !== undefined;
          }
        }

        const originalValue = cell.getValue();
        const displayedValue = hasPendingChange ? pendingValue : originalValue;

        // U11 (user-reported): meaning always owns the ring, selection
        // always owns the fill — see `selectionAffordance.ts`. A pending
        // edit keeps its orange `MEANING_OUTLINE_CLASS` ring whether or
        // not the cell is selected; selecting it only changes the FILL
        // to the blue selection wash, replacing the orange one.
        const cellFill = resolveCellFill(
          ctx.isCellSelected(actualRowIndex, columnId),
          hasPendingChange,
        );

        return (
          <td
            key={cell.id}
            // `data-row-index` / `data-column-id` are read by the grid-level
            // delegated context menu to resolve which cell was right-clicked.
            data-row-index={actualRowIndex}
            data-column-id={columnId}
            // `group/cell` scopes the PP-10 hover reveal of the cell expand
            // affordance (mirrored from Table Explorer) — scoped rather than
            // the unscoped `group` so a future row-level `group` can't
            // cross-trigger it.
            //
            // PP-12 parity: the pending-edit wash is `--energy-soft`, the
            // wave's sanctioned "work in flight" orange token.
            //
            // U11 (user-reported) two-grid parity: meaning ALWAYS owns the
            // ring (unconditional `MEANING_OUTLINE_CLASS` whenever
            // `hasPendingChange`), selection ALWAYS owns the fill. Decided in
            // JS so exactly one background utility is ever emitted.
            //
            // The `bg-accent/50` open-menu wash used to ride on Radix's
            // per-cell `data-[state=open]` — with the delegated single menu it
            // is applied directly to the one cell the menu is open on.
            className={`px-3 py-1 border-r border-grid-rule last:border-r-0 group/cell
                       whitespace-nowrap text-foreground overflow-hidden text-ellipsis relative
                       ${menuColumnId === columnId ? 'bg-accent/50' : ''}
                       ${numericColumnIds.has(columnId) ? 'text-right' : ''}
                       ${hasPendingChange ? MEANING_OUTLINE_CLASS : ''}
                       ${cellFill === 'meaning' ? 'bg-energy-soft' : ''}
                       ${cellFill === 'selection' ? 'bg-primary/35' : ''}
                       ${isCellInlineEditable ? 'cursor-text' : ''}`}
            // Widths come from the grid's <colgroup> (fixed layout); the
            // min/max here only shape the initial auto layout.
            style={{
              minWidth: minColumnWidth,
              maxWidth: maxAutoColumnWidth,
              ...pinnedCellStyle,
            }}
            title={cellTitle(displayedValue)}
            onMouseDown={(e) => {
              ctx.onCellMouseDown(actualRowIndex, columnId, e);
            }}
            onMouseEnter={() => {
              ctx.onCellMouseEnter(actualRowIndex, columnId);
            }}
            onDoubleClick={(e) => {
              if (isCellInlineEditable) {
                e.stopPropagation();
                ctx.onStartInlineEdit(actualRowIndex, columnId);
              }
            }}
          >
            {isEditing ? (
              <InlineCellEditor
                initialValue={displayedValue}
                onCommit={(newValue) => {
                  ctx.onInlineEditCommit(row.original, columnId, originalValue, newValue);
                }}
                onCancel={ctx.onInlineEditCancel}
                smartContext={ctx.buildSmartContext(columnId, row.original)}
              />
            ) : hasPendingChange ? (
              (() => {
                const col = columnById.get(columnId);
                if (displayedValue === null || displayedValue === undefined) {
                  return <span className="text-muted-foreground/60 italic text-xs">NULL</span>;
                }
                if (typeof displayedValue === 'object') {
                  const jsonStr = JSON.stringify(displayedValue);
                  const isPlainObjOrArray = Array.isArray(displayedValue)
                    || Object.getPrototypeOf(displayedValue) === Object.prototype
                    || Object.getPrototypeOf(displayedValue) === null;
                  if (isPlainObjOrArray || jsonStr.length >= 80) {
                    return (
                      <span className={CELL_EXPAND_WRAPPER_CLASS}>
                        <button
                          type="button"
                          className={CELL_EXPAND_BUTTON_CLASS}
                          title="View JSON"
                          aria-label="View JSON"
                          data-testid="qe-cell-expand-btn"
                          onClick={(event) => {
                            event.stopPropagation();
                            ctx.onOpenExpandedText(col?.name ?? columnId, jsonStr, columnId, row.original, actualRowIndex);
                          }}
                          onDoubleClick={(event) => event.stopPropagation()}
                        >
                          <Code2 className="h-3 w-3" />
                        </button>
                        <span className="font-mono text-xs truncate min-w-0">{jsonStr}</span>
                      </span>
                    );
                  }
                  return <span className="font-mono text-xs truncate min-w-0">{jsonStr}</span>;
                }
                const rawStrValue = String(displayedValue);
                const strValue = groupedNumericColumnIds.has(columnId)
                  ? formatNumericDisplay(displayedValue, numericGrouping) ?? rawStrValue
                  : rawStrValue;
                const isJsonLike = typeof displayedValue === 'string'
                  && looksLikeJsonString(displayedValue);
                if (isJsonLike) {
                  return (
                    <span className={CELL_EXPAND_WRAPPER_CLASS}>
                      <button
                        type="button"
                        className={CELL_EXPAND_BUTTON_CLASS}
                        title="View JSON"
                        aria-label="View JSON"
                        data-testid="qe-cell-expand-btn"
                        onClick={(event) => {
                          event.stopPropagation();
                          ctx.onOpenExpandedText(col?.name ?? columnId, displayedValue as string, columnId, row.original, actualRowIndex);
                        }}
                        onDoubleClick={(event) => event.stopPropagation()}
                      >
                        <Code2 className="h-3 w-3" />
                      </button>
                      <span className="truncate min-w-0">{strValue}</span>
                    </span>
                  );
                }
                if (typeof displayedValue === 'string' && shouldShowExpandIconForQueryResults(displayedValue, {
                  isEditMode: editMode?.isEditMode && editMode?.isColumnEditable(columnId),
                  dataType: col?.dataType,
                })) {
                  const isPendingJson = looksLikeJsonString(displayedValue);
                  const PendingIconComponent = isPendingJson ? Code2 : Maximize2;
                  return (
                    <span className={CELL_EXPAND_WRAPPER_CLASS}>
                      <button
                        type="button"
                        className={CELL_EXPAND_BUTTON_CLASS}
                        title={isPendingJson ? 'View JSON' : 'View expanded'}
                        aria-label={isPendingJson ? 'View JSON' : 'View expanded'}
                        data-testid="qe-cell-expand-btn"
                        onClick={(event) => {
                          event.stopPropagation();
                          ctx.onOpenExpandedText(col?.name ?? columnId, displayedValue as string, columnId, row.original, actualRowIndex);
                        }}
                        onDoubleClick={(event) => event.stopPropagation()}
                      >
                        <PendingIconComponent className="h-3 w-3" />
                      </button>
                      <span className="truncate min-w-0">{strValue}</span>
                    </span>
                  );
                }
                return strValue;
              })()
            ) : (
              renderCellContent(cell)
            )}
          </td>
        );
      })}
      {/* PP-05 filler — inert: no handlers, not a TanStack column,
          so cell selection and every copy/export path skip it.
          `width: 100%` matches the header filler so the column
          stays greedy in auto layout (see `GridHeaderRow`).
          `select-none` keeps it out of a *native* text selection:
          without it Chromium serialises the empty cell as a
          trailing tab, so drag-selecting a row and copying
          yielded `1\tAlice\t` instead of `1\tAlice` (measured). */}
      <td
        aria-hidden="true"
        data-grid-filler="true"
        className="p-0 border-0 select-none"
        style={{ width: '100%' }}
      />
    </tr>
  );
}, resultsGridDataRowPropsEqual);
